import { Injectable } from '@angular/core';
import { AbstractSearchService } from '@forgerock/backstage-ui-core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { EverythingSearchParams, EverythingSearchParamsOptions } from './model/everything-search-params';
import {
  AggregationResult,
  EverythingSearchResult,
  EverythingSearchResultContainer
} from './model/everything-search-result';

@Injectable({
  providedIn: 'root'
})
export class EverythingSearchService<T = EverythingSearchResult> extends AbstractSearchService<T, EverythingSearchResultContainer<T>> {
  override maxResults = 5000;
  override params = new EverythingSearchParams({
    aggregation: [ 'source', 'publicationType', 'library', 'priority', 'status', 'entityType',
      'project', 'group', 'tags' ]
  });
  override url = '/search-ws/api/v1/everything/_search';
  data$ = new BehaviorSubject<EverythingSearchResultContainer<T>>(this.data);

  /**
   * Sets the new params in the state, which in turn updates the params in this object.
   * The params are meant to be immutable, and each change replaces it with a new object.
   */
  withParams(params: Partial<EverythingSearchParamsOptions>): this {
    if (params) {
      this.params = new EverythingSearchParams({ ...this.params, ...params });
    }
    return this;
  }

  async searchAsync(): Promise<EverythingSearchResultContainer<T>> {
    const res = await firstValueFrom(this.search());
    Object.assign(this.data, res);
    this.data$.next(this.data);
    return res;
  }

  get aggregations(): Partial<Record<keyof EverythingSearchResult, AggregationResult[]>> {
    return this.data.aggregations?.reduce((acc, next) => ({ ...acc, [ next.name ]: next.results }), {});
  }
}
